import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, FileBadge, Loader2, Paperclip, Pencil, Plus, RefreshCw, Trash2, UploadCloud } from 'lucide-react';
import { Drawer } from '@components/Drawer';
import { Button } from '@components/Button';
import { ConfirmDialog } from '@components/Dialog';
import { ActionButton } from '@components/Table';
import { FileTypeIcon } from '@components/FileTypeIcon';
import { CacStatusBadge, ExpiryLabel } from '@components/ExpiryBadge';
import { usePermissions } from '@hooks/usePermissions';
import firearmService from '@services/firearmService';
import documentService, { CAC_FOLDER_NAME, findFolderByName } from '@services/documentService';
import { DocumentUploadModal } from '@pages/documents/DocumentUploadModal';
import { downloadDocument, openDocumentInNewTab } from '@pages/documents/documentActions';
import type { DocumentFolder } from '@/types/document';
import { CAC_DOC_TYPE_LABELS, FIREARM_KIND_LABELS, FIREARM_STATUS_LABELS, firearmLabel, type CacDocument, type Firearm } from '@/types/cac';
import { formatBRL } from '@/utils/money';
import { formatBytes, formatDateBR } from '@/utils/formatters';
import { FirearmFormModal } from './FirearmFormModal';
import { CacDocumentFormModal, type CacFormMode } from './CacDocumentFormModal';

interface Props {
  firearmId: number;
  onClose: () => void;
  /** Avisa a lista para recarregar (edição, exclusão, documento novo). */
  onChanged: () => void;
  onError: (err: unknown) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className="text-sm text-slate-800 [overflow-wrap:anywhere]">{children}</p>
  </div>
);

const STATUS_BADGE = { active: 'badge-success', sold: 'badge-neutral', transferred: 'badge-info' } as const;

/**
 * Ficha da arma (GET /firearms/:id): dados, documentos CAC (todos, inclusive renovados) e arquivos
 * vinculados (`ref_type = firearm`), com upload direto para a pasta "Armas (CAC)".
 */
export const FirearmDrawer: React.FC<Props> = ({ firearmId, onClose, onChanged, onError }) => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('acervo.write').hasPermission;
  const canUpload = checkPermission('documentos.write').hasPermission;
  const [firearm, setFirearm] = useState<Firearm | null>(null);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cacForm, setCacForm] = useState<{ mode: CacFormMode; source?: CacDocument } | null>(null);
  const [actionBusy, setActionBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFirearm(await firearmService.getById(firearmId));
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firearmId]);

  useEffect(() => {
    load();
    documentService.getFolders().then(setFolders).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const cacFolder = findFolderByName(folders, CAC_FOLDER_NAME);
  const modalOpen = editing || deleting || uploading || !!cacForm;

  const changed = () => {
    load();
    onChanged();
  };

  const confirmDelete = async () => {
    if (!firearm) return;
    setDeleteBusy(true);
    try {
      await firearmService.remove(firearm.id);
      setDeleting(false);
      onChanged();
      onClose();
    } catch (err) {
      setDeleting(false);
      onError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const run = async (id: number, fn: (id: number) => Promise<void>) => {
    setActionBusy(id);
    try {
      await fn(id);
    } catch (err) {
      onError(err);
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <>
      <Drawer
        isOpen
        onClose={onClose}
        closeOnEsc={!modalOpen}
        size="lg"
        title={firearm ? firearmLabel(firearm) : 'Arma'}
        headerActions={
          canWrite && firearm ? (
            <>
              <Button variant="outline" size="sm" icon={<Pencil />} onClick={() => setEditing(true)}>
                Editar
              </Button>
              <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeleting(true)} />
            </>
          ) : undefined
        }
      >
        {loading && !firearm ? (
          <p className="text-center text-slate-400 py-10">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        ) : firearm ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Tipo">{FIREARM_KIND_LABELS[firearm.kind]}</Field>
              <Field label="Situação">
                <span className={STATUS_BADGE[firearm.status]}>{FIREARM_STATUS_LABELS[firearm.status]}</span>
              </Field>
              <Field label="Calibre">{firearm.caliber}</Field>
              <Field label="Nº de série">{firearm.serial_number}</Field>
              <Field label="SIGMA">{firearm.sigma_number || '—'}</Field>
              <Field label="Aquisição">
                {firearm.acquisition_date ? formatDateBR(firearm.acquisition_date) : '—'}
                {firearm.acquisition_value_cents !== null && firearm.acquisition_value_cents !== undefined && (
                  <span className="text-slate-500"> · {formatBRL(firearm.acquisition_value_cents)}</span>
                )}
              </Field>
              {firearm.notes && (
                <div className="col-span-2 sm:col-span-3">
                  <Field label="Observações">
                    <span className="whitespace-pre-wrap">{firearm.notes}</span>
                  </Field>
                </div>
              )}
            </div>

            {/* Documentos CAC da arma */}
            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <FileBadge size={16} className="text-slate-500" /> Documentos CAC
                </h3>
                {canWrite && (
                  <Button variant="outline" size="sm" icon={<Plus />} onClick={() => setCacForm({ mode: 'create' })}>
                    Novo
                  </Button>
                )}
              </div>
              {(firearm.cac_documents ?? []).length === 0 ? (
                <p className="text-small">Nenhum CRAF ou guia de tráfego cadastrado para esta arma.</p>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {(firearm.cac_documents ?? []).map((d) => (
                    <li key={d.id} className="p-3 flex flex-wrap items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800">
                          {CAC_DOC_TYPE_LABELS[d.doc_type]}
                          {d.number && <span className="text-slate-500 font-normal"> · nº {d.number}</span>}
                        </p>
                        <p className="text-small">
                          <ExpiryLabel expiresAt={d.expires_at} days={d.status === 'renewed' ? null : d.days_to_expire} />
                          {d.issuer_unit && ` · ${d.issuer_unit}`}
                        </p>
                      </div>
                      <CacStatusBadge status={d.status} />
                      {canWrite && d.status !== 'renewed' && (
                        <div className="flex items-center">
                          <ActionButton icon={<RefreshCw size={15} />} variant="view" tooltip="Renovar" onClick={() => setCacForm({ mode: 'renew', source: d })} />
                          <ActionButton icon={<Pencil size={15} />} variant="edit" tooltip="Editar" onClick={() => setCacForm({ mode: 'edit', source: d })} />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <Link to={`/cac/documents?firearm_id=${firearm.id}`} className="text-xs text-emerald-700 hover:underline">
                Ver em Documentos CAC
              </Link>
            </section>

            {/* Arquivos vinculados */}
            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <Paperclip size={16} className="text-slate-500" /> Arquivos
                </h3>
                {canUpload && (
                  <Button variant="outline" size="sm" icon={<UploadCloud />} onClick={() => setUploading(true)}>
                    Enviar
                  </Button>
                )}
              </div>
              {(firearm.documents ?? []).length === 0 ? (
                <p className="text-small">Nenhum arquivo vinculado (nota fiscal, fotos, laudos…).</p>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {(firearm.documents ?? []).map((d) => (
                    <li key={d.id} className="p-2 sm:p-3 flex items-center gap-2">
                      <FileTypeIcon fileName={d.file_name} contentType={d.content_type} size={16} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-800 truncate">{d.title}</p>
                        <p className="text-small truncate">
                          {d.file_name} · {formatBytes(d.size_bytes)}
                        </p>
                      </div>
                      <ActionButton icon={<ExternalLink size={15} />} variant="view" tooltip="Visualizar" disabled={actionBusy === d.id} onClick={() => run(d.id, openDocumentInNewTab)} />
                      <ActionButton icon={<Download size={15} />} variant="default" tooltip="Baixar" disabled={actionBusy === d.id} onClick={() => run(d.id, downloadDocument)} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>

      {editing && firearm && (
        <FirearmFormModal
          firearm={firearm}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            changed();
          }}
          onError={onError}
        />
      )}

      {uploading && firearm && (
        <DocumentUploadModal
          folders={folders}
          prefill={{ folder_id: cacFolder?.id, lockFolder: !!cacFolder, ref_type: 'firearm', ref_id: firearm.id }}
          onClose={() => {
            setUploading(false);
            changed();
          }}
          onUploaded={() => {
            setUploading(false);
            changed();
          }}
          onError={onError}
        />
      )}

      {cacForm && firearm && (
        <CacDocumentFormModal
          mode={cacForm.mode}
          source={cacForm.source}
          firearms={[firearm]}
          prefillFirearmId={firearm.id}
          onClose={() => setCacForm(null)}
          onSaved={() => {
            setCacForm(null);
            changed();
          }}
          onError={onError}
        />
      )}

      <ConfirmDialog
        isOpen={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={confirmDelete}
        title="Excluir arma"
        message={`Excluir "${firearm ? firearmLabel(firearm) : ''}" do acervo? Não é possível excluir uma arma com documento CAC vigente.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleteBusy}
      />
    </>
  );
};
